<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  GpMenuItemAction,
  GpMenuItemBtn,
  GpMenuItemContent,
  GpMenuItemDivider,
  GpMenuItemGroupHeader,
  GpTag,
  GpTooltip,
} from '@gportal/uikit';
import SessionUserAvatar from '@panel/components/app/SessionUserAvatar.vue';
import type { ServiceError } from 'gpcore-apikit/grpc/types';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import { type Session, useSessionStore } from 'gpcore-apikit/store/pinia/session';
import { useUserStore } from 'gpcore-apikit/store/pinia/user';
import { ProjectMemberRole } from 'gpcore-grpc/gpcore/api/cloud/v1/project_pb';
import ImpersonatingTag from 'gpcore-uikit/components/new/header/ImpersonatingTag.vue';
import UserMenu from 'gpcore-uikit/components/new/header/UserMenu.vue';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import { computed, ref } from 'vue';

const projectStore = useProjectStore();
const project = computed(() => projectStore.project);
const membership = computed(() => projectStore.currentUserMembership);

const sessionStore = useSessionStore();
const session = computed(() => sessionStore.session);
const isAdmin = computed(() => {
  return sessionStore.hasRole('super-admin');
});

const projectHasBilling = computed(() => projectStore.hasBillingProfile);
const userNeedsBilling = computed(() => {
  return (
    project.value &&
    membership.value &&
    membership.value.role === ProjectMemberRole.OWNER &&
    !projectHasBilling.value
  );
});

const userMenu = ref<InstanceType<typeof UserMenu>>();
const userStore = useUserStore();
const { addNotification } = useNotifications();
const resendConfirmMail = () => {
  userMenu.value.hideMenu();
  userStore
    .resendConfirmEMail()
    .then(() => {
      addNotification({
        type: 'success',
        message: 'Please check your inbox. We sent you a new confirmation email.',
      });
    })
    .catch((service: ServiceError) => {
      addNotification({
        type: 'danger',
        message: service.message,
      });
    });
};

const isMainSession = (sessionId: string) => {
  return sessionStore.mainSession.id === sessionId;
};

const isValidSession = (sessionId: string) => {
  // is main session, is always valid
  if (isMainSession(sessionId)) {
    return true;
  }

  // is impersonated session, check, if the token is expired in relation to client time
  if (session.value.impersonated) {
    return session.value.expiresAt != null && session.value.expiresAt * 1000 > Date.now();
  }

  return true;
};

const additionalSessions = computed<
  Array<{
    id: string;
    user: Session['user'];
    impersonated: boolean;
    isMainSession: boolean;
    isExpired: boolean;
  }>
>(() =>
  [sessionStore.mainSession, ...sessionStore.sessions]
    .filter((s) => {
      if (s.user == null || session.value == null) {
        return false;
      }

      return s.id !== session.value.id;
    })
    .map(({ id, user, impersonated }) => {
      return {
        id,
        user,
        impersonated,
        isMainSession: isMainSession(id),
        isExpired: isValidSession(id),
      };
    }),
);

const switchSession = (sessionId: string) => {
  sessionStore.switchSession(sessionId);
  window.location.reload();
};

const isImpersonating = computed(() => sessionStore.isImpersonating);

const navigateViaMenu = ($event, routerNavigateFn) => {
  $event.preventDefault();
  userMenu.value.hideMenu();

  routerNavigateFn();
};
</script>

<template>
  <UserMenu
    v-if="session.user != null"
    ref="userMenu"
    :avatar-url="session.user.avatarUrl"
    :full-name="session.user.fullName"
    :username="session.user.username"
    :email="session.user.email"
    :is-admin="!isImpersonating && isAdmin"
    :is-impersonating="isImpersonating"
    :has-warning="!session.user.confirmed || userNeedsBilling"
    @logout="sessionStore.logout()"
  >
    <GpMenuItemBtn
      v-if="!session.user.confirmed"
      type="button"
      class="tw-cursor-pointer tw-bg-gp-orange tw-px-4 tw-py-1 !tw-text-gp-black tw-outline-none hover:tw-bg-gp-orange-light"
      @click="resendConfirmMail"
    >
      <FontAwesomeIcon :icon="['fas', 'exclamation-triangle']" class="tw-mr-2" />
      Resend confirm e-mail
    </GpMenuItemBtn>

    <template v-if="additionalSessions.length > 0">
      <GpMenuItemGroupHeader label="Other sessions" dense />
      <template v-for="session in additionalSessions">
        <GpMenuItemBtn
          v-if="session.user"
          :key="session.id"
          @click="switchSession(session.id)"
          class="tw-my-2 tw-px-4 tw-py-2 tw-transition-colors"
          :class="{
            'tw-cursor-pointer hover:tw-bg-gp-dark-mouse-light': session.isExpired,
            'tw-cursor-not-allowed tw-opacity-50': !session.isExpired,
          }"
        >
          <div class="tw-relative">
            <SessionUserAvatar style="--gp-avatar-size: 40px" />

            <GpTag
              v-if="session.isMainSession"
              label="YOU"
              tag-type="info"
              size="xs"
              class="tw-absolute -tw-bottom-1 -tw-right-1"
            />

            <GpTooltip
              v-else-if="session.impersonated"
              content="You are impersonating this user."
              placement="top"
              trigger="hover"
              class="tw-absolute -tw-bottom-1 -tw-right-1"
            >
              <ImpersonatingTag />
            </GpTooltip>
          </div>
          <p>
            {{ session.user.fullName || session.user.username || session.user.email }}
            <br />
            <GpTag v-if="!session.isExpired" label="SESSION EXPIRED" tag-type="danger" size="xs" />
          </p>
        </GpMenuItemBtn>
      </template>
      <GpMenuItemDivider />
    </template>

    <!-- settings menu -->
    <GpMenuItemAction v-slot="{ contentClass }">
      <router-link :to="{ name: 'UserSettings' }" custom v-slot="{ href, navigate }">
        <GpMenuItemContent
          component="a"
          :href="href"
          :class="contentClass"
          :icon="['fas', 'wrench']"
          label="Settings"
          @click="($event) => navigateViaMenu($event, navigate)"
        />
      </router-link>
    </GpMenuItemAction>

    <GpMenuItemAction v-slot="{ contentClass }">
      <router-link :to="{ name: 'ProjectList' }" v-slot="{ href, navigate }" custom>
        <GpMenuItemContent
          component="a"
          :href="href"
          :class="contentClass"
          label="Projects"
          :icon="['fas', 'fa-briefcase']"
          @click="($event) => navigateViaMenu($event, navigate)"
        />
      </router-link>
    </GpMenuItemAction>

    <GpMenuItemDivider />
  </UserMenu>
</template>

<style scoped></style>
